<script lang="ts">
  import type { Config } from '@pin/schemas/common';
  import type {
    ClientToServerEvent as OverlayClientToServerEvents,
    ServerToClientEvents as OverlayServerToClientEvents,
  } from '@pin/schemas/overlay';
  import type { PollState } from '@pin/schemas/poll';
  import type { ChatMessageAdmin } from '@pin/schemas/socket';
  import type { StickyMessageState } from '@pin/schemas/stickyMessage';
  import { format } from 'date-fns';
  import type { Socket } from 'socket.io-client';
  import { onMount } from 'svelte';
  import { pollPolls } from '../../api/polls.api';
  import { stickyMessages$ } from '../../api/stickyMessage.api';
  import { now } from '../../stores/now';
  import { formatElapsed } from '../../utils/elapsed';
  import type { ChatMessage } from '../../utils/history';
  import { localStorageWritable } from '../../utils/localstoreWritable';
  import UserFeed from '../UserFeed.svelte';
  import BansPanel from './BansPanel.svelte';
  import BlocklistPanel from './BlocklistPanel.svelte';
  import ControlPanel from './ControlPanel.svelte';
  import MutesPanel from './MutesPanel.svelte';
  import PollPanel from './PollPanel.svelte';
  import StickyMessagePanel from './StickyMessagePanel.svelte';

  interface Props {
    overlaySocket: Socket<
      OverlayServerToClientEvents,
      OverlayClientToServerEvents
    > | null;
    config: Config | null;
    chatMessages: ChatMessage[];
    userFeedUser: ChatMessageAdmin['user'] | null;
  }
  let {
    overlaySocket,
    config,
    chatMessages,
    userFeedUser = $bindable(),
  }: Props = $props();

  const expanded = localStorageWritable<string[]>('setting-panels-expanded', [
    'settings',
  ]);

  let polls = $state<PollState | null>(null);
  let stickyMessages = $state<StickyMessageState | null>(null);

  const togglePanel = (
    panel: string,
    e: Event & { currentTarget: EventTarget & HTMLDetailsElement }
  ) => {
    const target = e.target as HTMLDetailsElement;
    expanded.update((current) => {
      if (target.open) {
        if (!current.includes(panel)) {
          return [...current, panel];
        }
        return current;
      } else {
        return current.filter((p) => p !== panel);
      }
    });
  };

  onMount(() => {
    const pollSubscription = pollPolls().subscribe({
      next: (data) => (polls = data),
      error: (err) => console.error('Poll subscription error', err),
    });
    const stickyMessageSubscription = stickyMessages$.subscribe({
      next: (data) => (stickyMessages = data),
      error: (err) => console.error('Sticky message subscription error', err),
    });
    return () => {
      pollSubscription.unsubscribe();
      stickyMessageSubscription.unsubscribe();
    };
  });
</script>

<div class="container">
  <h2 class="header">Info</h2>
  <div class="top-section">
    <details
      class="top-detail"
      open={$expanded.includes('settings')}
      ontoggle={(e) => togglePanel('settings', e)}
    >
      <summary><strong>Settings</strong></summary>
      {#if $expanded.includes('settings')}
        {#if config == null}
          <p>Loading config....</p>
        {:else}
          <ControlPanel {config} />
        {/if}
      {/if}
    </details>
    <details
      class="top-detail"
      open={$expanded.includes('polls')}
      ontoggle={(e) => togglePanel('polls', e)}
    >
      <summary>
        <strong>Polls</strong>
        {#if polls?.live != null}
          <span class="active-tag"></span>
          {#if polls.liveAtTs != null}
            <span
              class="timer"
              title="Live in the chat since {format(
                polls.liveAtTs,
                'HH:mm:ss'
              )}">{formatElapsed($now - polls.liveAtTs)}</span
            >
          {/if}
        {:else if polls?.lastLiveAtTs != null}
          <span class="timer"
            >{formatElapsed($now - polls.lastLiveAtTs)} ago</span
          >
        {/if}
      </summary>
      {#if $expanded.includes('polls')}
        <PollPanel {polls} />
      {/if}
    </details>
    <details
      class="top-detail"
      open={$expanded.includes('sticky-message')}
      ontoggle={(e) => togglePanel('sticky-message', e)}
    >
      <summary>
        <strong>Sticky Message</strong>
        {#if stickyMessages?.live != null}
          <span class="active-tag"></span>
          {#if stickyMessages.liveAtTs != null}
            <span
              class="timer"
              title="Live in the chat since {format(
                stickyMessages.liveAtTs,
                'HH:mm:ss'
              )}">{formatElapsed($now - stickyMessages.liveAtTs)}</span
            >
          {/if}
        {:else if stickyMessages?.lastLiveAtTs != null}
          <span class="timer"
            >{formatElapsed($now - stickyMessages.lastLiveAtTs)} ago</span
          >
        {/if}
      </summary>
      {#if $expanded.includes('sticky-message')}
        <StickyMessagePanel {stickyMessages} />
      {/if}
    </details>
    <details
      class="top-detail"
      open={$expanded.includes('blocklist')}
      ontoggle={(e) => togglePanel('blocklist', e)}
    >
      <summary><strong>Banned Words</strong></summary>
      {#if $expanded.includes('blocklist')}
        <BlocklistPanel />
      {/if}
    </details>
    <details
      class="top-detail"
      open={$expanded.includes('bans')}
      ontoggle={(e) => togglePanel('bans', e)}
    >
      <summary><strong>Banned Users</strong></summary>
      {#if $expanded.includes('bans')}
        <BansPanel />
      {/if}
    </details>
    <details
      class="top-detail"
      open={$expanded.includes('mutes')}
      ontoggle={(e) => togglePanel('mutes', e)}
    >
      <summary><strong>Muted Users</strong></summary>
      {#if $expanded.includes('mutes')}
        <MutesPanel />
      {/if}
    </details>
  </div>
  {#if userFeedUser != null}
    <div class="bottom-section">
      <UserFeed {chatMessages} {overlaySocket} bind:userFeedUser />
    </div>
  {/if}
</div>

<style>
  .container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0.5rem;
    gap: 0.5rem;
  }
  .header {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }
  .top-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    background: #1e2a2f;
    border-radius: 6px;
    flex: 1;
    overflow-y: auto;
  }
  .top-detail {
    background: #344349;
    border-radius: 0.5rem;
    padding: 1rem 1rem;
  }
  .bottom-section {
    background: #1c1c1c;
    padding: 8px;
    border-radius: 6px;
    flex: 1;
    overflow-y: auto;
  }

  summary {
    display: flex;
    align-items: center;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    user-select: none;
  }

  .timer {
    font-size: 0.75rem;
    font-weight: 400;
    color: var(--color-zinc-300);
    margin-left: 0.5rem;
    font-variant-numeric: tabular-nums;
  }

  .active-tag {
    background: var(--color-red-500);
    height: 8px;
    width: 8px;
    border-radius: 9999px;
    margin-left: 0.5rem;
    flex-shrink: 0;
  }
</style>
